import { useEffect, useState } from 'react'
import { Logger } from '@/services/Logger'
import './SimProviderPage.css'
import { PAGES } from '@/constants/pages'
import PermissionGuard from '@/components/PermissionGuard'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { usePartner } from '@/contexts/partnerContext'
import { PartnerSimProviderService } from '@/services/PartnerSimProviderService'
import { SimProvider } from '@/models/simProvider'
import { PageLayout } from './PageLayout'
import { useLoading } from '@/contexts/LoadingContext'
import toast from '@/utils/toast'; 

export default function SimProviderPage() {
  const { showLoader, hideLoader } = useLoading()

  // Core state
  const [simProviders, setSimProviders] = useState<SimProvider[]>([])
  const { impersonatedPartnerId } = usePartner();

  // Modal states
  const [createProviderModal, setCreateProviderModal] = useState({
    show: false,
    loading: false,
    form: { name: '' },
    errors: {} as { [key: string]: string }
  })

  const [updateProviderModal, setUpdateProviderModal] = useState({
    show: false,
    loading: false,
    providerId: '',
    form: { name: '' },
    errors: {} as { [key: string]: string }
  })

  const [deleteProviderModal, setDeleteProviderModal] = useState({
    show: false,
    loading: false,
    providerId: '',
    providerName: ''
  })

  // Form validation
  const validateProviderForm = (name: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}

    if (!name.trim()) {
      errors.name = 'Provider name is required'
    } else if (name.length > 200) {
      errors.name = 'Provider name must be 200 characters or less'
    }

    // Check for duplicates
    const isDuplicate = simProviders.some(provider =>
      provider.name.toLowerCase() === name.toLowerCase().trim()
    )
    if (isDuplicate) {
      errors.name = 'A SIM provider with this name already exists'
    }

    return errors
  }

  // Form handlers
  const handleCreateFormChange = (field: 'name', value: string) => {
    const updatedForm = { ...createProviderModal.form, [field]: value }
    const errors = validateProviderForm(updatedForm.name)
    setCreateProviderModal(prev => ({
      ...prev,
      form: updatedForm,
      errors
    }))
  }

  const openCreateModal = () => {
    setCreateProviderModal({
      show: true,
      loading: false,
      form: { name: '' },
      errors: {}
    })
  }


  const handleUpdateFormChange = (value: string) => {
    const errors = validateProviderForm(value)
    setUpdateProviderModal(prev => ({
      ...prev,
      form: { name: value },
      errors
    }))
  }

  const shouldGetSimProviders = impersonatedPartnerId !== null || authService.channelPartnerId !== null;

  const fetchSimProviders = async () => {
    let active = true;

    if (!shouldGetSimProviders) {
      active = false;
      setSimProviders([]);
      return;
    };

    try {
      showLoader()
      const data = await (await PartnerSimProviderService.getAll()).simProviders
      if (active) {
        if (data == null || data.length == 0) {
          setSimProviders([])
        } else {
        setSimProviders(data.sort((a, b) => a.name.localeCompare(b.name)))
        }
      }
    } catch (error) {
      toast.error('Failed to fetch sim providers.');
    } finally {
      if (active) {
        hideLoader()
        active = false;
      }
    }
  }

  useEffect(() => {
      fetchSimProviders();
    }, [impersonatedPartnerId]); 

  const handleCreateProvider = async () => {
    const errors = validateProviderForm(createProviderModal.form.name)
    if (Object.keys(errors).length > 0) {
      setCreateProviderModal(prev => ({ ...prev, errors }))
      return
    }

    setCreateProviderModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerSimProviderService.create(createProviderModal.form.name.trim())
      toast.success('Sim provider has successfully been created.');
      fetchSimProviders();
      setCreateProviderModal({
        show: false,
        loading: false,
        form: { name: '' },
        errors: {}
      })
    } catch (err) {
      const error = err as Error
      // Check if this is a duplicate name error
      if (error.message.includes('already exists')) {
        setCreateProviderModal(prev => ({
          ...prev,
          errors: { name: 'A SIM provider with this name already exists' }
        }))
      }
    } finally {
      setCreateProviderModal(prev => ({ ...prev, loading: false }))
    }
  }

  const handleUpdateProvider = async () => {
    const errors = validateProviderForm(updateProviderModal.form.name)
    if (Object.keys(errors).length > 0) {
      setUpdateProviderModal(prev => ({ ...prev, errors }))
      return
    }

    setUpdateProviderModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerSimProviderService.update(updateProviderModal.providerId,updateProviderModal.form.name.trim())
      fetchSimProviders();
      toast.success('Sim provider has successfully been updated.');
      setUpdateProviderModal({
        show: false,
        loading: false,
        providerId: '',
        form: { name: '' },
        errors: {}
      })
    } catch (err) {
      const error = err as Error
      Logger.error('SimProviderPage: Failed to update sim provider', { error: err })

      // Check if this is a duplicate name error
      if (error.message.includes('already exists')) {
        setUpdateProviderModal(prev => ({
          ...prev,
          errors: { name: 'A SIM provider with this name already exists' }
        }))
      }
    } finally {
      setUpdateProviderModal(prev => ({ ...prev, loading: false }))
    }
  }

  const handleDeleteProvider = async () => {
    setDeleteProviderModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerSimProviderService.delete(deleteProviderModal.providerId)
      toast.success('Sim provider has successfully been deleted.');
      // Refresh data
      fetchSimProviders();

      // Close modal
      setDeleteProviderModal({
        show: false,
        loading: false,
        providerId: '',
        providerName: ''
      })
    } catch (err) {
      toast.error('Failed to delete sim provider');
    } finally {
      setDeleteProviderModal(prev => ({ ...prev, loading: false }))
    }
  }

  return (
    <PageLayout
      title={PAGES.SIM_PROVIDERS.title}
      heading={PAGES.SIM_PROVIDERS.title}
      action={
        shouldGetSimProviders && (
          <PermissionGuard
          resource={[RESOURCES.SIM_PROVIDERS]}
          scope={PermissionScope.CREATE}
        >
          <button className="create-provider-btn" onClick={openCreateModal}>
            Add SIM Provider
          </button>
          </PermissionGuard>
        )
      }
    >

        <div className="sim-provider-list">
              {/* SIM Provider List or Empty State */}
              {simProviders.length === 0 ? (
                <div className="empty-state">
                  <p>No SIM providers found.</p>
                </div>
              ) : (
                <div className="provider-table">
                  <div className="table-header">
                    <div className="name-column">Name</div>
                      <PermissionGuard resource={RESOURCES.SIM_PROVIDERS} scope={PermissionScope.UPDATE}>
                          <div className="action-column">Update</div>
                      </PermissionGuard>
                      <PermissionGuard resource={RESOURCES.SIM_PROVIDERS} scope={PermissionScope.DELETE}>
                        <div className="action-column">Delete</div>
                      </PermissionGuard>
                  </div>
                  {simProviders.map((provider) => (
                    <div key={provider.id} className="table-row">
                      <div className="name-column">
                        <span className="provider-name">{provider.name}</span>
                        <span className="provider-date">
                          Created: {new Date(provider.createdAtUtc).toLocaleDateString()}
                        </span>
                      </div>
                          <div className="action-column">
                          <PermissionGuard resource={RESOURCES.SIM_PROVIDERS} scope={PermissionScope.UPDATE}>
                            <button
                                className="edit-btn"
                                onClick={() => setUpdateProviderModal({
                                  show: true,
                                  loading: false,
                                  providerId: provider.id,
                                  form: { name: provider.name },
                                  errors: {}
                                })}
                              >
                                Update
                              </button>
                          </PermissionGuard>
                          </div>
                          <div className="action-column">
                          <PermissionGuard resource={RESOURCES.SIM_PROVIDERS} scope={PermissionScope.DELETE}>
                            <button
                                className="delete-btn"
                                onClick={() => setDeleteProviderModal({
                                  show: true,
                                  loading: false,
                                  providerId: provider.id,
                                  providerName: provider.name
                                })}
                              >
                                Delete
                              </button>
                          </PermissionGuard>
                          </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

    {createProviderModal.show && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Add SIM Provider</h3>
                <button
                  className="close-btn"
                  onClick={() => setCreateProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={createProviderModal.loading}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="create-name">Provider Name</label>
                  <input
                    id="create-name"
                    type="text"
                    value={createProviderModal.form.name}
                    onChange={(e) => handleCreateFormChange('name', e.target.value)}
                    className={createProviderModal.errors.name ? 'error' : ''}
                    disabled={createProviderModal.loading}
                    placeholder="e.g., Vodafone, EE, O2"
                  />
                  {createProviderModal.errors.name && (
                    <span className="error-text">{createProviderModal.errors.name}</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="cancel-btn"
                  onClick={() => setCreateProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={createProviderModal.loading}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleCreateProvider}
                  disabled={createProviderModal.loading || Object.keys(createProviderModal.errors).some(key => createProviderModal.errors[key])}
                >
                  {createProviderModal.loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

      {updateProviderModal.show && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Edit SIM Provider</h3>
                <button
                  className="close-btn"
                  onClick={() => setUpdateProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={updateProviderModal.loading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="update-name">Provider Name</label>
                  <input
                    id="update-name"
                    type="text"
                    value={updateProviderModal.form.name}
                    onChange={(e) => handleUpdateFormChange(e.target.value)}
                    className={updateProviderModal.errors.name ? 'error' : ''}
                    disabled={updateProviderModal.loading}
                  />
                  {updateProviderModal.errors.name && (
                    <span className="error-text">{updateProviderModal.errors.name}</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="cancel-btn"
                  onClick={() => setUpdateProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={updateProviderModal.loading}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleUpdateProvider}
                  disabled={updateProviderModal.loading || Object.keys(updateProviderModal.errors).some(key => updateProviderModal.errors[key])}
                >
                  {updateProviderModal.loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Provider Modal */}
        {deleteProviderModal.show && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Delete SIM Provider</h3>
                <button
                  className="close-btn"
                  onClick={() => setDeleteProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={deleteProviderModal.loading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the SIM provider "<strong>{deleteProviderModal.providerName}</strong>"?</p>
                <p className="warning-text">This action cannot be undone. Devices using this provider will need to be updated.</p>
              </div>
              <div className="modal-footer">
                <button
                  className="cancel-btn"
                  onClick={() => setDeleteProviderModal(prev => ({ ...prev, show: false }))}
                  disabled={deleteProviderModal.loading}
                >
                  Cancel
                </button>
                <button
                  className="delete-btn"
                  onClick={handleDeleteProvider}
                  disabled={deleteProviderModal.loading}
                >
                  {deleteProviderModal.loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
    </PageLayout>
  )
}
